import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Seo } from "@/seo/Seo";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Loader2, Check, Star } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { reviewAccess, commercialSelect } from "@/lib/api";

const KEYS = [
  ["minimal", "01 — Minimal Solution"],
  ["recommended", "02 — Recommended Solution"],
  ["gtm_ready", "03 — GTM Ready Solution"],
];

export default function Review() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [selecting, setSelecting] = useState("");
  const [selected, setSelected] = useState("");
  const [workspaceUrl, setWorkspaceUrl] = useState("");

  const open = async () => {
    if (!/\S+@\S+\.\S+/.test(email)) return toast.error("Enter the email you used on your intake.");
    setBusy(true);
    try { setData(await reviewAccess(token, email.trim())); }
    catch (e) { toast.error(e?.response?.data?.detail || "Not available yet."); }
    finally { setBusy(false); }
  };

  const choose = async (key) => {
    setSelecting(key);
    try {
      const res = await commercialSelect(token, email.trim(), key);
      setSelected(key);
      setWorkspaceUrl(res.workspace_url || "");
      toast.success("Solution selected. Your commercial proposal is being prepared.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not select."); }
    finally { setSelecting(""); }
  };

  const Header = () => (
    <header className="border-b border-[#141414] sticky top-0 z-40 bg-black/80 backdrop-blur-xl">
      <Seo noindex path="/review" title="Product Review · AIROTECH" />
      <div className="max-w-4xl mx-auto px-6 h-[72px] flex items-center">
        <button onClick={() => navigate("/")}><img src="/airotech-mark.png" alt="AIROTECH" className="h-8 w-auto" /></button>
      </div>
    </header>
  );

  if (!data) return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <div className="max-w-xl mx-auto px-6 pt-20">
        <div className="mono-label text-cyan-400 mb-6">AIROTECH PRODUCT REVIEW</div>
        <h1 className="font-heading font-extrabold tracking-tighter text-3xl md:text-5xl">Access your Product Review.</h1>
        <p className="mt-5 text-zinc-400">Confirm the email you used on your intake to view your solution recommendations.</p>
        <div className="mt-8 space-y-3">
          <Label className="mono-label text-zinc-400">Your email</Label>
          <Input data-testid="review-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && open()}
            className="bg-[#0b0b0c] border-[#1f1f1f] text-white h-12 focus-visible:ring-cyan-400/40 focus-visible:border-cyan-400/60" placeholder="you@company.com" />
        </div>
        <button data-testid="review-open" onClick={open} disabled={busy}
          className="mt-6 inline-flex items-center gap-2 bg-white text-black font-medium px-7 py-4 rounded-full mono-label hover:bg-cyan-400 transition-colors duration-200 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>View Review <ArrowRight className="h-4 w-4" /></>}
        </button>
      </div>
    </div>
  );

  const r = data.review || {};
  return (
    <div data-testid="review-root" className="min-h-screen bg-[#050505] text-white">
      <Header />
      <div className="max-w-4xl mx-auto px-6 py-14">
        <div className="mono-label text-cyan-400 mb-4">AIROTECH PRODUCT REVIEW · SOLUTION DEFINITION & RECOMMENDATIONS</div>
        <h1 className="font-heading font-extrabold tracking-tighter text-4xl md:text-6xl">{r.product_definition ? "Your product." : "Product Review."}</h1>
        {r.executive_summary && <p className="mt-6 text-lg text-zinc-300 leading-relaxed">{r.executive_summary}</p>}
        {r.what_we_heard && <><div className="mono-label text-zinc-500 mt-12 mb-3">WHAT WE HEARD</div><p className="text-zinc-400 leading-relaxed">{r.what_we_heard}</p></>}
        {r.opportunity && <><div className="mono-label text-zinc-500 mt-10 mb-3">THE OPPORTUNITY</div><p className="text-zinc-400 leading-relaxed">{r.opportunity}</p></>}

        <div className="mono-label text-cyan-400 mt-16 mb-6">THREE SOLUTION PATHS</div>
        <div className="space-y-5">
          {KEYS.map(([k, title]) => {
            const s = r[k] || {}; const isRec = data.recommended_solution_key === k;
            return (
              <motion.div key={k} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                data-testid={`solution-${k}`}
                className={`rounded-2xl border p-7 md:p-9 ${isRec ? "border-cyan-400/50 bg-cyan-400/[0.05]" : "border-[#161616] bg-[#0a0a0a]"}`}>
                <div className="flex items-center justify-between gap-4">
                  <div className="mono-label text-white">{title}</div>
                  {isRec && <span className="mono-label text-cyan-300 inline-flex items-center gap-1.5"><Star className="h-3.5 w-3.5" /> RECOMMENDED BY AIROTECH</span>}
                </div>
                <div className="mt-3 font-heading text-2xl text-white">{s.solution_name || title}</div>
                <p className="mt-3 text-zinc-400 leading-relaxed">{s.summary}</p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <span className="mono-label text-cyan-400 bg-cyan-400/10 border border-cyan-400/20 rounded-full px-3 py-1.5">{s.build_level_label}</span>
                  <span className="mono-label text-zinc-300 border border-[#232323] rounded-full px-3 py-1.5">{s.investment_range}</span>
                </div>
                {Array.isArray(s.core_capabilities) && (
                  <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-2">
                    {s.core_capabilities.map((c) => (
                      <li key={c} className="flex items-start gap-2.5 text-sm text-zinc-400"><Check className="h-4 w-4 mt-0.5 text-cyan-400 shrink-0" />{c}</li>
                    ))}
                  </ul>
                )}
                {s.why_this_solution && <p className="mt-5 text-sm text-zinc-500"><span className="text-zinc-300">Why: </span>{s.why_this_solution}</p>}
                {s.tradeoffs && <p className="mt-2 text-sm text-zinc-500"><span className="text-zinc-300">Tradeoffs: </span>{s.tradeoffs}</p>}
                <button data-testid={`select-solution-${k}`} onClick={() => choose(k)} disabled={!!selecting}
                  className={`mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full mono-label transition-colors disabled:opacity-50 ${selected === k ? "bg-cyan-400 text-black" : "border border-[#232323] text-white hover:border-cyan-400/60"}`}>
                  {selecting === k ? <Loader2 className="h-4 w-4 animate-spin" /> : selected === k ? <><Check className="h-4 w-4" /> Selected</> : <>Select this solution <ArrowRight className="h-4 w-4" /></>}
                </button>
              </motion.div>
            );
          })}
        </div>

        {selected && (
          <div data-testid="review-selected-cta" className="mt-10 rounded-2xl border border-cyan-400/40 bg-cyan-400/[0.05] p-7 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <div className="mono-label text-cyan-300">SOLUTION SELECTED</div>
              <p className="mt-1 text-zinc-300 text-sm">We're preparing your commercial proposal. Track everything in your workspace.</p>
            </div>
            <button data-testid="review-goto-workspace" onClick={() => navigate(workspaceUrl ? new URL(workspaceUrl).pathname + new URL(workspaceUrl).search : `/workspace?token=${token}`)}
              className="inline-flex items-center gap-2 bg-white text-black font-medium px-6 py-3 rounded-full mono-label hover:bg-cyan-400 transition-colors whitespace-nowrap">
              Go to my workspace <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {r.recommendation_rationale && (
          <div className="mt-14 rounded-2xl border border-[#161616] bg-[#070707] p-8">
            <div className="mono-label text-cyan-400 mb-3">AIROTECH'S RECOMMENDATION</div>
            <p className="text-lg text-zinc-200">{(r[data.recommended_solution_key] || {}).solution_name}</p>
            <p className="mt-3 text-zinc-400 leading-relaxed">{r.recommendation_rationale}</p>
            {r.recommended_next_step && <p className="mt-5 mono-label text-zinc-500">NEXT STEP — <span className="text-cyan-300">{r.recommended_next_step}</span></p>}
          </div>
        )}
        <p className="mt-8 text-xs text-zinc-600">Investment ranges are build-level estimates, not final quotes. Timeline requires further planning after commercial approval.</p>
      </div>
    </div>
  );
}
